import { useEffect, useState } from 'react';
import type { EnlaceSalida, IdiomaSalida, OperacionesMisEntradas } from '../api/ops/misEntradas';
import { ErrorApi } from '../api/problema';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { Ventana } from '../componentes/Ventana';
import { AvisoPc, Baldosa, BotonPc, EnlaceLegal } from './Pagina';
import { ENLACES_LEGALES } from './textos';
import { cuandoEvento, idiomaInicial, SALIDA } from './textosEntradas';
import './cliente.css';

/**
 * «Salir de la lista» (10.16, G755, ADR-0195): página pública de Reservaria en `/salir-de-la-lista/{token}` (G767), con
 * el enlace personal de T43 o del pase por email. Enseña negocio, evento, cuándo y lista (nunca datos del invitado) y
 * **abrirla no escribe nada** (G155): solo `getGuestLeaveLink`. «Salir de la lista» pide confirmación y llama a
 * `confirmGuestLeave`. Lista cerrada (`can_leave: false` o `409`): sin botón, con «Privacidad de [negocio]» (`privacy_url`,
 * G788) para saber a quién escribir. Español e inglés (`Accept-Language`).
 */
type Estado = { tipo: 'cargando' } | { tipo: 'error' } | { tipo: 'ido' } | { tipo: 'listo'; info: EnlaceSalida; cerrada: boolean } | { tipo: 'hecho'; info: EnlaceSalida };

interface Props {
    ops: Pick<OperacionesMisEntradas, 'getGuestLeaveLink' | 'confirmGuestLeave'>;
    token: string;
    busqueda?: URLSearchParams;
    movil?: boolean;
}

export function SalirDeLaLista({ ops, token, busqueda, movil = true }: Props) {
    const [idioma, setIdioma] = useState<IdiomaSalida>(() => idiomaInicial(busqueda, typeof navigator === 'undefined' ? undefined : navigator.language));
    const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [confirmar, setConfirmar] = useState(false);
    const [saliendo, setSaliendo] = useState(false);
    const [aviso, setAviso] = useState<string | null>(null);
    const t = SALIDA[idioma];

    useEffect(() => {
        let vivo = true;
        ops.getGuestLeaveLink(token, idioma)
            .then((info) => {
                if (vivo) {
                    setEstado((e) => (e.tipo === 'hecho' ? e : { tipo: 'listo', info, cerrada: !info.can_leave }));
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setEstado((x) => (x.tipo === 'hecho' ? x : e instanceof ErrorApi && e.estado === 404 ? { tipo: 'ido' } : { tipo: 'error' }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, token, idioma, intento]);

    const salir = (info: EnlaceSalida) => {
        setSaliendo(true);
        setAviso(null);
        ops.confirmGuestLeave(token)
            .then(() => {
                setConfirmar(false);
                setEstado({ tipo: 'hecho', info });
            })
            .catch((e: unknown) => {
                setConfirmar(false);
                if (e instanceof ErrorApi && e.estado === 409) {
                    setEstado({ tipo: 'listo', info, cerrada: true });
                } else if (e instanceof ErrorApi && e.estado === 404) {
                    setEstado({ tipo: 'ido' });
                } else if (e instanceof ErrorApi && e.estado === 429) {
                    setAviso(t.demasiados);
                } else {
                    setAviso(t.errTexto);
                }
            })
            .finally(() => { setSaliendo(false); });
    };

    const filas = (info: EnlaceSalida): [NombreIcono, string, string][] => [
        ['store', t.negocio, info.business_name],
        ['calendar', t.evento, info.event_name],
        ['clock', t.cuando, cuandoEvento(info.event_starts_at, info.event_timezone, idioma)],
        ['list-checks', t.lista, info.guest_list_name],
    ];

    let cuerpo;
    if (estado.tipo === 'cargando') {
        cuerpo = (
            <section className="rv-pc-card" aria-busy="true" aria-label={t.cargando}>
                <div className="rv-pc-esqueleto" style={{ height: 32 }} />
                <div className="rv-pc-esqueleto" style={{ height: 180, marginTop: 16 }} />
            </section>
        );
    } else if (estado.tipo === 'error') {
        cuerpo = (
            <section className="rv-pc-card">
                <div className="rv-pc-card__cab"><h1>{t.errTitulo}</h1><span>{t.errTexto}</span></div>
                <BotonPc texto={t.reintentar} icono="refresh-cw" onClick={() => { setEstado({ tipo: 'cargando' }); setIntento((i) => i + 1); }} />
            </section>
        );
    } else if (estado.tipo === 'ido') {
        cuerpo = (
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila"><Baldosa icono="link" fondo="var(--rv-paper)" color="var(--rv-ink)" /></div>
                <div className="rv-pc-card__cab"><h1>{t.idoTitulo}</h1><span>{t.idoTexto}</span></div>
            </section>
        );
    } else if (estado.tipo === 'hecho') {
        cuerpo = (
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila"><Baldosa icono="circle-check" fondo="var(--rv-ink)" color="#fff" /></div>
                <div className="rv-pc-card__cab"><h1>{t.hechoTitulo}</h1><span>{t.hechoTexto(estado.info.business_name)}</span></div>
            </section>
        );
    } else {
        const { info, cerrada } = estado;
        cuerpo = (
            <section className="rv-pc-card" aria-labelledby="rv-salir-t">
                <p className="rv-pc-ceja">{t.ceja}</p>
                <div className="rv-pc-card__cab"><h1 id="rv-salir-t">{cerrada ? t.tituloCerrada : t.titulo}</h1></div>
                <div className="rv-pc-lineas">
                    {filas(info).map(([icono, etiqueta, valor]) => (
                        <div key={etiqueta} className="rv-pc-linea">
                            <Icono nombre={icono} />
                            <span className="rv-pc-fila__texto"><span>{etiqueta}</span><strong>{valor}</strong></span>
                        </div>
                    ))}
                </div>
                {cerrada ? (
                    <div className="rv-pc-caja">
                        <strong className="rv-pc-caja__titulo"><Icono nombre="lock" /> {t.cerradaTitulo}</strong>
                        <span>{t.cerradaTexto(info.business_name)}</span>
                        {info.privacy_url != null && <EnlaceLegal url={info.privacy_url}>{t.privacidadDe(info.business_name)}</EnlaceLegal>}
                    </div>
                ) : (
                    <>
                        <p className="rv-pc-nota"><span>{t.pregunta}</span></p>
                        {aviso !== null && <AvisoPc texto={aviso} />}
                        <BotonPc texto={t.salir} icono="user-minus" onClick={() => { setConfirmar(true); }} />
                    </>
                )}
            </section>
        );
    }

    const info = estado.tipo === 'listo' || estado.tipo === 'hecho' ? estado.info : null;
    return (
        <div className="rv-final rv-pc" data-pagina-cliente="" lang={idioma}>
            <div className="rv-vivo rv-vivo--ola" aria-hidden="true" />
            <div className="rv-pc__col">
                <header className="rv-final-osc rv-pc__cab">
                    <span className="rv-pc__logo"><span className="rv-ofrecido__logo" role="img" aria-label="Reservaria" /></span>
                    <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" aria-label={t.idiomaAria} onClick={() => { setIdioma(idioma === 'es' ? 'en' : 'es'); }}>
                        <Icono nombre="languages" />
                        <span>{t.idioma}</span>
                    </button>
                </header>
                <main className="rv-pc__main rv-pc__main--centro">{cuerpo}</main>
                <footer className="rv-final-osc rv-pc__pie">
                    <span className="rv-pc__pie-enlaces">
                        {info?.privacy_url != null && (
                            <>
                                <EnlaceLegal url={info.privacy_url}>{t.privacidadDe(info.business_name)}</EnlaceLegal>
                                <span aria-hidden="true">·</span>
                            </>
                        )}
                        <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{t.privacidadReservaria}</EnlaceLegal>
                    </span>
                </footer>
            </div>
            {info !== null && (
                <Ventana
                    abierta={confirmar}
                    titulo={t.confirmarTitulo}
                    texto={t.confirmarTexto}
                    cancelar={t.quedarme}
                    accion={t.salirCorto}
                    accionIcono="user-minus"
                    ocupado={saliendo}
                    ocupadoTexto={t.saliendo}
                    movil={movil}
                    onAccion={() => { salir(info); }}
                    onCerrar={() => { setConfirmar(false); }}
                />
            )}
        </div>
    );
}
